import axios from 'axios';
import { auth } from './firebase';

const TOKEN_TIMEOUT_MS = 3000;
const AUTH_HYDRATION_TIMEOUT_MS = 5000;

// Cache hydration promise — resolves once, reused across all requests
let hydrationPromise = null;

const ensureAuthReady = () => {
  if (auth.currentUser) return Promise.resolve();
  if (!hydrationPromise) {
    hydrationPromise = Promise.race([
      auth.authStateReady(),
      new Promise((resolve) => {
        setTimeout(() => {
          console.warn('[Auth] Hydration timeout (5s) — proceeding without auth');
          resolve();
        }, AUTH_HYDRATION_TIMEOUT_MS);
      })
    ]);
  }
  return hydrationPromise;
};

const getTokenWithTimeout = (user, forceRefresh = false) => {
  return Promise.race([
    user.getIdToken(forceRefresh),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('token_timeout')), TOKEN_TIMEOUT_MS);
    })
  ]);
};

// Protected path prefixes that require auth token on GET requests
const PROTECTED_GET_PREFIXES = [
  '/api/captain/',
  '/api/admin/',
  '/api/users/',
  '/api/upload/'
];

// Determine if a request needs authentication
const needsAuth = (config) => {
  const method = (config.method || 'get').toLowerCase();

  // POST, PUT, DELETE always need token
  if (method !== 'get') return true;

  // For GET requests, only protected paths need token
  try {
    let pathname = '';
    const url = config.url || '';
    if (url.startsWith('http')) {
      pathname = new URL(url).pathname;
    } else {
      pathname = url.split('?')[0];
    }
    return PROTECTED_GET_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  } catch (e) {
    return false;
  }
};

// Automatically attach Firebase ID token to protected requests only
axios.interceptors.request.use(async (config) => {
  if (!needsAuth(config)) return config;

  try {
    await ensureAuthReady();
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await getTokenWithTimeout(user);
        config.headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        console.warn('[Auth] Token fetch failed, retrying with force refresh');
        try {
          const token = await getTokenWithTimeout(user, true);
          config.headers.Authorization = `Bearer ${token}`;
        } catch (retryErr) {
          console.error('[Auth] Token retry failed — proceeding without token');
        }
      }
    }
  } catch (e) {
    // Fail-safe — never block requests
  }
  return config;
});
